import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/content";
import { CTAButton } from "@/components/ui-bits";
import { cn } from "@/lib/utils";

const Logo = ({ dark }) => (
  <a href="#home" data-testid="logo-link" className="flex items-center shrink-0">
    <img
      src={dark ? "/logo-white.png" : "/logo-color.png"}
      alt="SML Municipal Consulting"
      className="h-9 md:h-10 w-auto"
    />
  </a>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled;

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "fixed top-0 inset-x-0 z-50 transition-all duration-300",
          solid ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_2px_20px_rgba(15,23,42,0.05)]" : "bg-transparent"
        )}
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-[72px]">
            <Logo dark={!solid} />

            <nav className="hidden lg:flex items-center gap-7">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-${l.href.slice(1)}`}
                  className={cn(
                    "text-sm font-medium transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-secondary after:transition-all hover:after:w-full",
                    solid ? "text-slate-700 hover:text-primary" : "text-white/85 hover:text-white"
                  )}
                >
                  {l.label}
                </a>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                data-testid="header-call-link"
                className={cn("inline-flex items-center gap-2 text-sm font-semibold transition-colors", solid ? "text-primary hover:text-secondary" : "text-white hover:text-secondary")}
              >
                <Phone className="w-4 h-4" /> {CONTACT.phone}
              </a>
              <CTAButton href="#quote" variant="secondary" size="sm" testId="header-quote-btn">Request a Quote</CTAButton>
            </div>

            <button
              className={cn("lg:hidden p-2 rounded-md", solid ? "text-primary" : "text-white")}
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-primary/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-white shadow-2xl p-6 flex flex-col"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.4 }}
              data-testid="mobile-menu"
            >
              <div className="flex items-center justify-between mb-8">
                <Logo dark={false} />
                <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="p-2 text-primary">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.href.slice(1)}`}
                    className="py-3 text-lg font-medium text-slate-800 border-b border-slate-100 hover:text-secondary transition-colors"
                  >
                    {l.label}
                  </a>
                ))}
              </nav>
              <div className="mt-auto pt-8 flex flex-col gap-3">
                <CTAButton href="#quote" variant="secondary" onClick={() => setOpen(false)} testId="mobile-quote-btn" className="w-full">Request a Quote</CTAButton>
                <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center justify-center gap-2 py-3.5 rounded-full border border-slate-200 font-semibold text-primary">
                  <Phone className="w-4 h-4" /> {CONTACT.phone}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
